import React, { forwardRef } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

/* ─── Types ─────────────────────────────────────────────────── */
export interface BreadcrumbItem {
  label: string;
  href?: string;
}

export interface BreadcrumbProps extends React.HTMLAttributes<HTMLElement> {
  items: BreadcrumbItem[];
  separator?: React.ReactNode;
  maxItems?: number;
}

export const Breadcrumb = forwardRef<HTMLElement, BreadcrumbProps>(
  ({ className, items, separator, maxItems, ...props }, ref) => {
    const sep = separator ?? (
      <ChevronRight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
    );

    let displayItems = items;
    let truncated = false;

    if (maxItems && items.length > maxItems && maxItems >= 3) {
      const head = items.slice(0, 1);
      const tail = items.slice(-(maxItems - 2));
      displayItems = [
        ...head,
        { label: '…' },
        ...tail,
      ];
      truncated = true;
    }

    return (
      <nav ref={ref} aria-label="Breadcrumb" className={cn('text-sm', className)} {...props}>
        <ol className="flex items-center gap-1.5">
          {displayItems.map((item, i) => {
            const isLast = i === displayItems.length - 1;
            const isEllipsis = item.label === '…' && truncated;

            return (
              <li key={i} className="flex items-center gap-1.5">
                {i > 0 && <span className="flex items-center">{sep}</span>}

                {isEllipsis ? (
                  <span
                    className="text-gray-400 dark:text-gray-500 select-none"
                    aria-hidden="true"
                  >
                    …
                  </span>
                ) : isLast ? (
                  <span
                    className="font-medium text-gray-900 dark:text-gray-100"
                    aria-current="page"
                  >
                    {item.label}
                  </span>
                ) : item.href ? (
                  <a
                    href={item.href}
                    className={cn(
                      'text-gray-500 hover:text-gray-700 transition-colors',
                      'dark:text-gray-400 dark:hover:text-gray-200',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded-sm'
                    )}
                  >
                    {item.label}
                  </a>
                ) : (
                  <span className="text-gray-500 dark:text-gray-400">{item.label}</span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    );
  }
);

Breadcrumb.displayName = 'Breadcrumb';
